import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import Loader from '@/components/ui/Loader'
import { fetcher, putFetcher } from 'api/swr'

type BotPkOpponentGroup = {
  cptGroupStart: number
  cptGroupEnd: number
  avatarIds: string[]
}

const parseTids = (raw: string): string[] => {
  const parts = raw
    .split(/[\n,，\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
  return [...new Set(parts)].sort()
}

const BotPk: React.FC = () => {
  const { toast } = useToast()
  const {
    data: templates,
    isLoading: templatesLoading,
    mutate: mutateTemplates,
  } = useSWR<string[]>(['/ops-tools/bot-pk/templates'], fetcher)

  const [templatesText, setTemplatesText] = useState('')
  const [selectedTid, setSelectedTid] = useState<string | null>(null)

  const {
    data: selectedGroups,
    isLoading: groupsLoading,
    mutate: mutateGroups,
  } = useSWR<BotPkOpponentGroup[]>(
    selectedTid ? [`/ops-tools/bot-pk/opponents/${selectedTid}`] : null,
    fetcher,
  )

  useEffect(() => {
    if (templates) {
      setTemplatesText(templates.join('\n'))
    }
  }, [templates])

  const { trigger: saveTemplates, isMutating: isSaving } = useSWRMutation(
    '/ops-tools/bot-pk/templates',
    putFetcher,
  )

  const parsedPreview = useMemo(
    () => parseTids(templatesText),
    [templatesText],
  )

  const onSave = useCallback(async () => {
    try {
      const tids = parseTids(templatesText)
      await saveTemplates({ tids })
      await mutateTemplates(tids, false)
      toast({
        title: '已保存',
        description: `共 ${tids.length} 个模版`,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err)
      toast({
        variant: 'destructive',
        title: '保存失败',
        description: message,
      })
    }
  }, [mutateTemplates, saveTemplates, templatesText, toast])

  if (templatesLoading) {
    return <Loader />
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Bot PK 模版配置</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bot-pk-templates">
              模版 ID（每行一个，或用逗号分隔）
            </Label>
            <Textarea
              id="bot-pk-templates"
              className="min-h-[160px] font-mono text-xs"
              placeholder="粘贴模版 tid…"
              value={templatesText}
              onChange={(e) => setTemplatesText(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              已解析 {parsedPreview.length} 个 tid，写入 neku 业务 Redis SET
              `pk:bot:tpls`
            </p>
          </div>
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving ? '保存中…' : '保存模版列表'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>对手 Avatar 池（只读）</CardTitle>
          {selectedTid ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => mutateGroups()}
              disabled={groupsLoading}
            >
              刷新
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {!templates?.length ? (
            <p className="text-sm text-muted-foreground">
              暂无已配置的模版。请先在上方配置模版 tid。
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                {templates.map((tid) => (
                  <Button
                    key={tid}
                    variant={tid === selectedTid ? 'default' : 'outline'}
                    size="sm"
                    className="font-mono text-xs"
                    onClick={() =>
                      setSelectedTid((prev) => (prev === tid ? null : tid))
                    }
                  >
                    {tid}
                  </Button>
                ))}
              </div>

              {!selectedTid ? (
                <p className="text-sm text-muted-foreground">
                  点击上方某个模版 tid，按需加载其对手 avatar 池。
                </p>
              ) : groupsLoading ? (
                <Loader />
              ) : !selectedGroups?.length ? (
                <p className="text-xs text-muted-foreground">
                  该模版暂无对手池数据
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedGroups.map((group) => (
                    <div
                      key={`${selectedTid}-${group.cptGroupStart}`}
                      className="rounded-md border p-3"
                    >
                      <p className="mb-2 text-xs text-muted-foreground">
                        cptCount {group.cptGroupStart}–{group.cptGroupEnd}（
                        {group.avatarIds.length} 个）
                      </p>
                      <pre className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs">
                        {group.avatarIds.join('\n')}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default BotPk
